import { DateFilterParam } from "@pathwright/utils/src/param/param-configs"
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from "react"
import {
  NumberParam,
  createEnumParam,
  useQueryParams,
  withDefault
} from "use-query-params"
import useCompletions from "../../completion/graphql/useCompletions"
import { convertObjectKeysToSnakeCase } from "../../utils/convertObjectKeys"
import {
  CompletionFilterKey,
  getFiltersList
} from "../context/completion-filters"
import {
  UseReviewContextReturn,
  useReviewContext as _useReviewContext
} from "./useReviewContext"

type CompletionsContext = {
  stepId?: number
  sourceId?: number
  parentId?: number
  userId?: number
  cohortId?: number
  filter?: string
}

const getContextMeta = (/* { items, userId } */) => {
  // TODO: figure out a better way to handle top-level context meta... will likely require a query
  return {
    title: "All"
  }
}

export type UseReviewableCompletionsReturn = ReturnType<
  typeof useReviewableCompletions
>

const FilterEnumParam = createEnumParam([
  "all",
  "pending",
  "reviewed",
  "complete",
  "incomplete"
] satisfies CompletionFilterKey[])

type OrderParam = "asc" | "desc"

const OrderEnumParam = createEnumParam(["asc", "desc"] satisfies OrderParam[])

// This hook is responsible for the top-level loading of completions based on a given context
// it's job is to load all completion items from GQL and provide info. to the ReviewContext to take over from there
// it provides to context actions:
// - filter filter the existing context
// - update: updates/sets a new context
const useReviewableCompletions = (initialContext: CompletionsContext = {}) => {
  const [context, setContext] = useState(initialContext)

  const [query, setQuery] = useQueryParams({
    user_id: NumberParam,
    cohort_id: NumberParam,
    group_id: NumberParam,
    step_id: NumberParam,
    source_id: NumberParam,
    parent_id: NumberParam,
    filter: withDefault(FilterEnumParam, "pending"),
    order: withDefault(OrderEnumParam, "desc"),
    archived_date_filter: DateFilterParam
  })

  const { user_id, parent_id } = query

  const selectedFilter = query.filter
  const order = query.order

  const allFilters = getFiltersList()

  const contextQueryVars = {
    ...query,
    ...convertObjectKeysToSnakeCase(context),
    lesson_source_id: parent_id,
    order_by: query.order === "asc" ? "completion_date" : "-completion_date"
  }

  const {
    items,
    total,
    loading,
    error,
    hasMore,
    loadMore,
    loadingMore,
    refetch
  } = useCompletions(contextQueryVars)

  const filter = useCallback(
    (key: CompletionFilterKey, order?: OrderParam) => {
      setQuery({
        filter: key
      })
      if (order) {
        setQuery({
          order: order
        })
      }
    },
    [setQuery]
  )

  const hasParentContext = () => {
    let parentContext = contextQueryVars.cohort_id || contextQueryVars.group_id
    return !!parentContext
  }

  const backToParentContext = () => {
    if (hasParentContext()) {
      // reset all query params except for the top-level context (cohort_id or group_id)
      setQuery({
        user_id: undefined,
        parent_id: undefined,
        step_id: undefined,
        source_id: undefined
      })
    }
  }

  const reviewContext = useMemo(() => {
    let completions = items || []
    let ctx = {
      id: "context",
      filter,
      update: setContext,
      filters: allFilters,
      selectedFilter,
      order,
      total,
      loading,
      error,
      hasMore,
      loadMore,
      loadingMore,
      refetch,
      hasParentContext,
      backToParentContext,
      context,
      query: contextQueryVars,
      queryParams: query,
      setQueryParams: setQuery,
      items: completions,
      meta: getContextMeta(/* { completions, userId: user_id } */),
      depth: 0
    }
    return ctx
  }, [
    items,
    loading,
    loadingMore,
    hasMore,
    loadMore,
    refetch,
    selectedFilter,
    error
  ])

  return reviewContext
}

const ReviewContext = createContext<UseReviewContextReturn>(
  {} as UseReviewContextReturn
)

export const ReviewContextProvider = ({
  completionsContext,
  children
}: {
  completionsContext?: CompletionsContext
  children: React.ReactNode
}) => {
  const completionsReviewContext = useReviewableCompletions(completionsContext)
  let value = _useReviewContext(completionsReviewContext)
  return (
    <ReviewContext.Provider value={value}>{children}</ReviewContext.Provider>
  )
}

export const useReviewContext = () => {
  return useContext(ReviewContext)
}
